import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { documentApi } from 'src/api/document';
import FilesUploadModal, {
  FilesUploadModalFormValuesProps,
} from 'src/components/files-upload-modal';
import Iconify from 'src/components/iconify';
import ProfileFilesTable from './ProfileFilesTable';
import ProfileFilesTableSkeleton from './ProfileFilesTableSkeleton';
import { translateKey } from 'src/locales/translate';
import PatientNoteModal from './PatientNoteModal/PatientNoteModal';
import { useTable } from 'src/components/table';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';


export const ProfileFilesContent = () => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [noteModal, setNoteModal] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    filename?: string;
    content: string;
  }>({ open: false, mode: 'create', content: '' });
  const { enqueueSnackbar } = useSnackbar();
  const { profileId, patientId } = useParams<{
    profileId?: string;
    patientId?: string;
  }>();
  const actualProfileId = profileId || patientId; // Support both route params
  const queryClient = useQueryClient();

  // User fetching their own documents
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE,
  });

  const { data, isLoading, isError, remove } = useQuery(
    ['profileDocuments', actualProfileId, page, rowsPerPage],
    () =>
      documentApi.documentControllerGetProfileDocuments({
        profileId: actualProfileId as string,
        page,
        limit: rowsPerPage,
      }),
    {
      retry: 1,
      enabled: !!actualProfileId,
      // Keep the current page on screen while the next one loads.
      keepPreviousData: true,
    },
  );

  const { mutateAsync: upload, isLoading: isUploadLoading } = useMutation({
    mutationFn: (files: FilesUploadModalFormValuesProps['files']) =>
      documentApi.documentControllerAddProfileDocuments({
        profileId: actualProfileId as string,
        documentFileDto: files || [],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['profileDocuments', actualProfileId]);
      enqueueSnackbar(translateKey("hardcoded.pliki_zostaly_dodane"), {
        variant: 'success',
      });
      closeModal();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.message || translateKey("hardcoded.wystapil_blad_podczas_dodawania_plikow"),
        {
          variant: 'error',
        },
      );
    },
  });

  const { mutate: deleteDocument, isLoading: isDeleteDocumentLoading } =
    useMutation({
      mutationFn: (filename: string) =>
        documentApi.documentControllerDeleteProfileDocument({
          profileId: actualProfileId as string,
          filename,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries(['profileDocuments', actualProfileId]);
        enqueueSnackbar(translateKey("hardcoded.plik_zostal_usuniety"), {
          variant: 'success',
        });
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || translateKey("hardcoded.wystapil_blad_podczas_usuwania_pliku"),
          {
            variant: 'error',
          },
        );
      },
    });

  const { mutateAsync: createTextNote, isLoading: isCreatingNote } = useMutation({
    mutationFn: (content: string) =>
      documentApi.documentControllerCreateProfileTextNoteForOwner({
        profileId: actualProfileId as string,
        createTextNoteDto: { content },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['profileDocuments', actualProfileId]);
      enqueueSnackbar(translateKey('hardcoded.notatka_zostala_dodana'), {
        variant: 'success',
      });
      closeNoteModal();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message ||
          translateKey('hardcoded.wystapil_blad_podczas_zapisywania_notatki'),
        { variant: 'error' },
      );
    },
  });

  const { mutateAsync: updateTextNote, isLoading: isUpdatingNote } = useMutation({
    mutationFn: (vars: { filename: string; content: string }) =>
      documentApi.documentControllerUpdateProfileTextNoteForOwner({
        profileId: actualProfileId as string,
        filename: vars.filename,
        updateTextNoteDto: { content: vars.content },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['profileDocuments', actualProfileId]);
      enqueueSnackbar(translateKey('hardcoded.notatka_zostala_zaktualizowana'), {
        variant: 'success',
      });
      closeNoteModal();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message ||
          translateKey('hardcoded.wystapil_blad_podczas_zapisywania_notatki'),
        { variant: 'error' },
      );
    },
  });

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => setIsModalOpen(false);

  const openAddMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAddMenuAnchor(e.currentTarget);
  const closeAddMenu = () => setAddMenuAnchor(null);

  const handleAddFile = () => {
    closeAddMenu();
    openModal();
  };
  const handleAddNote = () => {
    closeAddMenu();
    setNoteModal({ open: true, mode: 'create', content: '' });
  };
  const openEditNote = (note: { filename: string; content: string }) =>
    setNoteModal({ open: true, mode: 'edit', filename: note.filename, content: note.content });
  const closeNoteModal = () => setNoteModal((s) => ({ ...s, open: false }));

  const handleNoteSubmit = async (content: string) => {
    if (noteModal.mode === 'edit' && noteModal.filename) {
      await updateTextNote({ filename: noteModal.filename, content });
    } else {
      await createTextNote(content);
    }
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const popoverOpen = Boolean(anchorEl);

  const handleOnUpload = async (
    files: FilesUploadModalFormValuesProps['files'],
  ) => {
    if (files) {
      await upload(files);
    }
  };

  const handleOnDelete = (filename: string) => {
    deleteDocument(filename);
  };

  useEffect(() => {
    if (!data && !isLoading && isError) {
      enqueueSnackbar(translateKey("hardcoded.nie_moglismy_pobrac_plikow_dla_wskazanego_profilu"), {
        variant: 'error',
      });
    }
  }, [data, isLoading, isError, enqueueSnackbar]);

  useEffect(() => {
    return () => {
      remove();
    };
  }, [remove]);

  return !isLoading ? (
    <>
      <Stack sx={CARD_HEADER_SX}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography {...CARD_HEADER_TITLE_PROPS}>
            {translateKey('search.book.promoDocsTitle')}
          </Typography>
          <IconButton
            size="small"
            onClick={handlePopoverOpen}
            sx={{ color: theme.palette.grey[500] }}
          >
            <Iconify icon="eva:info-outline" width={20} />
          </IconButton>
        </Stack>
        <Button
          size="small"
          startIcon={<Iconify icon="eva:plus-fill" width={16} />}
          variant="contained"
          sx={{
            background: theme.palette.common.black,
            color: theme.palette.common.white,
          }}
          onClick={openAddMenu}
          disabled={isUploadLoading}
        >{translateKey('common.add')}</Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={closeAddMenu}
        >
          <MenuItem onClick={handleAddFile}>
            {translateKey('hardcoded.plik')}
          </MenuItem>
          <MenuItem onClick={handleAddNote}>
            {translateKey('hardcoded.notatka')}
          </MenuItem>
        </Menu>
      </Stack>
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack p={2} maxWidth={400}>
          <Typography variant="body2" color="text.secondary">{translateKey("hardcoded.dodawaj_rozne_typy_dokumentacji_medycznej_swojego_pupila_takie")}</Typography>
        </Stack>
      </Popover>
      <ProfileFilesTable
        data={data?.data.data || []}
        page={page}
        rowsPerPage={rowsPerPage}
        total={data?.data.total ?? 0}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        onDelete={handleOnDelete}
        isDeleting={isDeleteDocumentLoading}
        profileId={actualProfileId}
        onEditTextNote={openEditNote}
      />
      <FilesUploadModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onUpload={handleOnUpload}
        isLoading={isUploadLoading}
        profileId={actualProfileId}
      />
      <PatientNoteModal
        isOpen={noteModal.open}
        onClose={closeNoteModal}
        mode={noteModal.mode}
        initialContent={noteModal.content}
        profileId={actualProfileId as string}
        onSubmit={handleNoteSubmit}
        isSaving={isCreatingNote || isUpdatingNote}
        canUseAssistant={false}
        enableDictation={false}
      />
    </>
  ) : (
    <>
      <Stack
        p={3}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Skeleton variant="rounded" height="24px" width="190px" />
        <Skeleton variant="rounded" height="36px" width="123px" />
      </Stack>
      <ProfileFilesTableSkeleton />
    </>
  );
};
